import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import {
  Box,
  Checkbox,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ElementType, ReactNode, useCallback } from "react";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";

export type PickerPopoverBaseProps<
  Item extends { id: string },
  T extends FieldValues,
  K extends Path<T>
> = {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  search: string;
  onSearchChange: (value: string) => void;
  loading: boolean;
  items: Item[];
  selectedIds: string[];
  allowMultiselect?: boolean;
  form: UseFormReturn<T>;
  /** The name of the field in the form to use for the picker. */
  formFieldName: K;
  /** The label to display when there are no items. */
  emptyLabel: string;
  /** Function to render the primary text of the item. */
  renderPrimary: (item: Item) => ReactNode;
  /** Optional function to render the secondary text of the item. */
  renderSecondary?: (item: Item) => ReactNode;
  /** Optional function to map the item to the value type. */
  mapValue?: (item: Item) => ElementType<PathValue<T, K>>;
  /** Optional callback triggered when scrolling near the bottom to load more items. */
  onLoadMore?: () => void;
  /** Whether there are more items available to load. */
  hasMore?: boolean;
  /** Whether currently loading more items. */
  loadingMore?: boolean;
};

export function PickerPopoverBase<
  Item extends { id: string },
  T extends FieldValues,
  K extends Path<T>
>({
  open,
  onClose,
  anchorEl,
  search,
  onSearchChange,
  loading,
  items,
  selectedIds,
  allowMultiselect = false,
  renderPrimary,
  renderSecondary,
  form,
  formFieldName,
  mapValue,
  emptyLabel,
  onLoadMore,
  hasMore,
  loadingMore,
}: PickerPopoverBaseProps<Item, T, K>) {
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const BUFFER_PX = 24;
      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      const bottom = scrollHeight <= scrollTop + clientHeight + BUFFER_PX;

      if (bottom && hasMore && !loadingMore) {
        onLoadMore?.();
      }
    },
    [hasMore, loadingMore, onLoadMore]
  );

  const onSelectionChange = useCallback(
    (nextItems: Item[]) => {
      if (allowMultiselect) {
        const nextValue = (mapValue
          ? nextItems.map((f) => mapValue(f))
          : nextItems) as unknown as PathValue<T, K>;
        form.setValue(formFieldName, nextValue, { shouldDirty: true });
      } else {
        if (nextItems.length === 0) {
          form.setValue(formFieldName, null as unknown as PathValue<T, K>, {
            shouldDirty: true,
          });
        } else {
          const value = (mapValue
            ? mapValue(nextItems[0])
            : nextItems[0]) as unknown as PathValue<T, K>;
          form.setValue(formFieldName, value, { shouldDirty: true });
        }
      }
    },
    [form, allowMultiselect, formFieldName, mapValue]
  );
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ "& .MuiPaper-root": { p: 1, width: 310 } }}
    >
      <Stack spacing={1}>
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
        />
        <Box sx={{ maxHeight: 320, overflow: "auto" }} onScroll={handleScroll}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress size={20} />
            </Stack>
          ) : items.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1, py: 1 }}
            >
              {emptyLabel}
            </Typography>
          ) : (
            <>
              <List dense disablePadding>
                {items.map((item) => {
                  const isSelected = !!selectedIds?.includes(item.id);
                  return (
                    <ListItemButton
                      key={item.id}
                      selected={isSelected}
                      sx={{ padding: 0 }}
                      onClick={() => {
                        const currentSelected =
                          selectedIds && selectedIds.length > 0
                            ? items.filter((i) => selectedIds.includes(i.id))
                            : [];
                        if (allowMultiselect) {
                          const next = isSelected
                            ? currentSelected.filter((i) => i.id !== item.id)
                            : [...currentSelected, item];
                          onSelectionChange(next);
                        } else {
                          if (isSelected) {
                            onSelectionChange([]);
                          } else {
                            onSelectionChange([item]);
                          }
                          onClose();
                        }
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        checkedIcon={<CheckCircleIcon />}
                        icon={<CircleOutlinedIcon />}
                      />
                      <ListItemText
                        primary={renderPrimary(item)}
                        secondary={
                          renderSecondary ? renderSecondary(item) : undefined
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
              {loadingMore && (
                <Stack alignItems="center" sx={{ py: 1 }}>
                  <CircularProgress size={16} />
                </Stack>
              )}
            </>
          )}
        </Box>
      </Stack>
    </Popover>
  );
}
